<script lang="ts" setup>
import { PropType, computed, ref, watch } from "vue";
import { useFlashMessage } from "@/hooks";
import { type Config } from "./Config";
import { Dropdown, Tooltip } from "@/components";
import {
  useDocumentSpaceCompatibilityGroupsQuery,
  useUpdateDocumentSpaceConfigSettingsMutation,
  useCreateDocumentSpaceConfigTranslationMutation,
  useDeleteDocumentSpaceConfigTranslationMutation,
} from "@/graphql/operations";
import { useForm, useField } from "vee-validate";
import * as yup from "yup";
import { useI18n } from "vue-i18n";
import CopyModal from "./CopyModal.vue";
import { useStore } from "vuex";
import TemplateUploadSettings from "@/views/template_editor/TemplateUploadSettings.vue";
import { normalizeOptionalMetadata } from "@/util/optionalMetadata";

const store = useStore();

const props = defineProps({
  config: {
    type: Object as PropType<Config>,
    required: true,
  },
});

const emits = defineEmits(["reloadData", "changeState"]);

const { t } = useI18n();

const availableLanguages = ["de", "en", "fr", "nl", "it", "pt"];

const getFormValues = (config: Config) => ({
  name: config.name,
  title: config.title,
  productName: config.productName ?? "",
  indication: config.indication ?? "",
  linkedTabOrder: config.linkedTabOrder,
  requiresEmailAuthentication: config.requiresEmailAuthentication,
  compatibilityGroupId: config.compatibilityGroupId,
  selectedLanguages: [...config.languages],
});

const validationSchema = yup.object({
  name: yup
    .string()
    .min(2, t("templateEditor.toolbar.settings.name.error"))
    .required(),
  title: yup
    .string()
    .min(2, t("templateEditor.toolbar.settings.displayTitle.error"))
    .required(),
  productName: yup.string(),
  indication: yup.string(),
  linkedTabOrder: yup
    .number()
    .typeError(t("templateEditor.toolbar.settings.tabOrder.error"))
    .integer(t("templateEditor.toolbar.settings.tabOrder.error"))
    .min(0, t("templateEditor.toolbar.settings.tabOrder.error"))
    .required(t("templateEditor.toolbar.settings.tabOrder.error")),
  requiresEmailAuthentication: yup.boolean(),
  compatibilityGroupId: yup.string().required(),
  selectedLanguages: yup.array().of(yup.string()),
});

const { meta, validate, resetForm } = useForm({
  validationSchema,
  initialValues: getFormValues(props.config),
});

const { value: name, errorMessage: nameError } = useField<string>("name");
const { value: title, errorMessage: titleError } = useField<string>("title");
const { value: productName } = useField<string>("productName");
const { value: indication } = useField<string>("indication");
const { value: linkedTabOrder, errorMessage: linkedTabOrderError } =
  useField<number>("linkedTabOrder");
const { value: requiresEmailAuthentication } = useField<boolean>(
  "requiresEmailAuthentication",
);
const { value: compatibilityGroupId } = useField<string>(
  "compatibilityGroupId",
);
const { value: selectedLanguages } = useField<string[]>("selectedLanguages");

const { displayFlashMessage } = useFlashMessage();

const displayCopyModal = ref(false);
const awaitingServerResync = ref(false);

const openCopyModal = () => {
  displayCopyModal.value = true;
};

const configSyncKey = computed(() =>
  JSON.stringify({
    baseConfigId: props.config.baseConfigId,
    name: props.config.name,
    title: props.config.title,
    productName: props.config.productName,
    indication: props.config.indication,
    linkedTabOrder: props.config.linkedTabOrder,
    requiresEmailAuthentication: props.config.requiresEmailAuthentication,
    compatibilityGroupId: props.config.compatibilityGroupId,
    selectedLanguages: [...props.config.languages].sort(),
  }),
);

watch(configSyncKey, () => {
  if (!awaitingServerResync.value && hasChanges.value) {
    return;
  }

  resetForm({
    values: getFormValues(props.config),
  });

  awaitingServerResync.value = false;
});

const isLanguageChecked = (languageCode: string) => {
  return selectedLanguages.value?.includes(languageCode) || false;
};

const toggleLanguage = (languageCode: string) => {
  if (languageCode === props.config.defaultLanguage) {
    return; // Can't toggle default language
  }

  if (isLanguageChecked(languageCode)) {
    selectedLanguages.value =
      selectedLanguages.value?.filter((l) => l !== languageCode) || [];
  } else {
    selectedLanguages.value = [
      ...(selectedLanguages.value || []),
      languageCode,
    ];
  }
};

const hasChanges = computed(() => {
  const languagesChanged =
    JSON.stringify([...props.config.languages].sort()) !==
    JSON.stringify([...(selectedLanguages.value || [])].sort());

  return (
    name.value !== props.config.name ||
    title.value !== props.config.title ||
    productName.value !== (props.config.productName ?? "") ||
    indication.value !== (props.config.indication ?? "") ||
    linkedTabOrder.value !== props.config.linkedTabOrder ||
    requiresEmailAuthentication.value !==
      props.config.requiresEmailAuthentication ||
    compatibilityGroupId.value !== props.config.compatibilityGroupId ||
    languagesChanged
  );
});

watch(
  hasChanges,
  (value) => {
    emits("changeState", value);
  },
  { immediate: true },
);

const { data: compatibilityGroupsData } =
  useDocumentSpaceCompatibilityGroupsQuery();
const compatibilityGroups = computed(
  () =>
    compatibilityGroupsData.value?.documentSpaceLinkGroups?.nodes?.map((g) => ({
      value: g!.id.toString(),
      title: g!.name,
    })) || [],
);

const setCompatibilityGroupId = (id: string) => {
  compatibilityGroupId.value = id;
};

const { executeMutation } = useUpdateDocumentSpaceConfigSettingsMutation();
const { executeMutation: createTranslation } =
  useCreateDocumentSpaceConfigTranslationMutation();
const { executeMutation: deleteTranslation } =
  useDeleteDocumentSpaceConfigTranslationMutation();

const submit = async () => {
  const { valid } = await validate();

  if (!valid) {
    return;
  }

  // Update basic settings
  const res = await executeMutation({
    id: props.config.baseConfigId,
    name: name.value,
    title: title.value,
    productName: normalizeOptionalMetadata(productName.value),
    indication: normalizeOptionalMetadata(indication.value),
    requiresEmailAuthentication: requiresEmailAuthentication.value,
    groupId: compatibilityGroupId.value,
  });

  if (res.error) {
    displayFlashMessage("Error updating settings", "error");
    return;
  }

  // Handle language changes
  const currentLanguages = props.config.languages;
  const newLanguages = selectedLanguages.value || [];

  // Find languages to add (in new but not in current)
  const languagesToAdd = newLanguages.filter(
    (lang) => !currentLanguages.includes(lang),
  );

  // Find languages to remove (in current but not in new, excluding default)
  const languagesToRemove = currentLanguages.filter(
    (lang) =>
      !newLanguages.includes(lang) && lang !== props.config.defaultLanguage,
  );

  // Create new translations
  for (const languageCode of languagesToAdd) {
    const createRes = await createTranslation({
      configId: props.config.baseConfigId,
      languageCode: languageCode,
    });

    if (createRes.error) {
      displayFlashMessage(
        `Error creating translation for ${languageCode}`,
        "error",
      );
    }
  }

  // Delete removed translations
  for (const languageCode of languagesToRemove) {
    const translation = props.config.translations.find(
      (t) => t.languageCode === languageCode,
    );
    if (translation) {
      const deleteRes = await deleteTranslation({
        id: translation.id,
      });

      if (deleteRes.error) {
        displayFlashMessage(
          `Error deleting translation for ${languageCode}`,
          "error",
        );
      }
    }
  }

  if (!res.error) {
    displayFlashMessage("Success", "success");
  }

  awaitingServerResync.value = true;
  emits("reloadData");
};
</script>

<template>
  <div class="toolbar_body">
    <div class="row">
      <div class="label_container">
        <label>{{ $t("templateEditor.toolbar.settings.name.label") }}</label>
      </div>
      <input type="text" class="input" v-model="name" />
      <p class="help" v-if="nameError">{{ nameError }}</p>
    </div>
    <div class="row">
      <div class="label_container">
        <label>{{
          $t("templateEditor.toolbar.settings.displayTitle.label")
        }}</label>
      </div>
      <input type="text" class="input" v-model="title" />
      <p class="help" v-if="titleError">{{ titleError }}</p>
    </div>
    <div class="row">
      <div class="label_container">
        <label for="classic-template-product-name">{{
          $t("templateEditor.toolbar.settings.productName.label")
        }}</label>
      </div>
      <input
        type="text"
        id="classic-template-product-name"
        class="input"
        data-test="classic-template-product-name"
        v-model="productName"
      />
    </div>
    <div class="row">
      <div class="label_container">
        <label for="classic-template-indication">{{
          $t("templateEditor.toolbar.settings.indication.label")
        }}</label>
      </div>
      <input
        type="text"
        id="classic-template-indication"
        class="input"
        data-test="classic-template-indication"
        v-model="indication"
      />
    </div>
    <div class="row">
      <div class="label_container">
        <label>{{
          $t("templateEditor.toolbar.settings.tabOrder.label")
        }}</label>
        <tooltip class="bottom left">
          <template v-slot>
            <span class="material-symbols-outlined info_icon"> info </span>
          </template>
          <template #tooltip>
            {{ $t("templateEditor.toolbar.settings.tabOrder.info") }}
          </template>
        </tooltip>
      </div>
      <input
        type="number"
        class="input"
        v-model.number="linkedTabOrder"
        min="0"
        step="1000"
      />
      <p class="help" v-if="linkedTabOrderError">{{ linkedTabOrderError }}</p>
    </div>
    <div class="row">
      <div class="label_container">
        <label>{{
          $t("templateEditor.toolbar.settings.compatibilityGroup.label")
        }}</label>
        <tooltip class="bottom left">
          <template v-slot>
            <span class="material-symbols-outlined info_icon"> info </span>
          </template>
          <template #tooltip>
            {{ $t("templateEditor.toolbar.settings.compatibilityGroup.info") }}
          </template>
        </tooltip>
      </div>
      <dropdown
        :items="compatibilityGroups"
        :value="compatibilityGroupId"
        :displayTitle="true"
        @change="setCompatibilityGroupId"
      />
    </div>
    <div
      class="row row_with_border"
      v-if="store.getters.user.hasTemplateEmailVerification()"
    >
      <div class="label_container">
        <label>{{
          $t("templateEditor.toolbar.settings.emailVerification.label")
        }}</label>
      </div>
      <label class="checkbox_label">
        <input type="checkbox" v-model="requiresEmailAuthentication" />
        {{ $t("templateEditor.toolbar.settings.emailVerification.toggle") }}
      </label>
      <p class="info">
        {{ $t("templateEditor.toolbar.settings.emailVerification.info") }}
      </p>
    </div>
    <div class="row row_with_border">
      <div class="label_container">
        <label>{{
          $t("templateEditor.toolbar.settings.languages.label")
        }}</label>
      </div>
      <div class="languages_list">
        <label
          v-for="languageCode in availableLanguages"
          :key="languageCode"
          class="checkbox_label"
          :class="{ disabled: languageCode === config.defaultLanguage }"
        >
          <input
            type="checkbox"
            :checked="isLanguageChecked(languageCode)"
            :disabled="languageCode === config.defaultLanguage"
            @change="toggleLanguage(languageCode)"
          />
          {{ $t(`templateEditor.languages.${languageCode}.label`) }}
        </label>
      </div>
    </div>
    <div class="row row_with_border">
      <div
        class="button secondary"
        role="button"
        tabindex="0"
        @keydown.enter.prevent="openCopyModal"
        @keydown.space.prevent="openCopyModal"
        @click="openCopyModal"
      >
        {{ $t("templateEditor.toolbar.settings.copyTemplate") }}
      </div>
    </div>
    <div
      v-if="store.getters.user.canConfigureHcpUploads()"
      class="row hcp_upload_settings classic-editor-shared-controls"
    >
      <TemplateUploadSettings
        classic-theme
        :template-id="config.baseConfigId"
        :template-name="config.name"
        @saved="emits('reloadData')"
      />
    </div>
  </div>
  <div class="toolbar_footer" @click="submit">
    <div
      class="button primary"
      :class="{ disabled: !hasChanges || !meta.valid }"
    >
      {{ $t("templateEditor.toolbar.settings.save") }}
    </div>
  </div>
  <CopyModal
    v-if="displayCopyModal"
    :config="config"
    @close="displayCopyModal = false"
    @reload="emits('reloadData')"
  />
</template>

<style lang="scss" scoped>
.toolbar {
  width: 300px;
  border-right: 1px solid var(--classic-border-subtle, #e0e0e0);
}

.toolbar_header {
  display: flex;
  border-bottom: 1px solid var(--classic-border-subtle, #e0e0e0);
  padding: 1em;
  background: var(--classic-page, #fafafa);
}

.toolbar_body {
  padding: 1em;
  font-size: 0.9rem;
  background: var(--classic-surface, #fff);
  height: calc(100vh - 60px - 60px - 82px);
  box-sizing: border-box;
  overflow-y: scroll;
}

.toolbar_footer {
  padding: 1em;
  font-size: 0.9rem;
  background: var(--classic-surface, #fff);
  border-top: 1px solid var(--classic-border-subtle, #e0e0e0);
}

.header_label {
  font-weight: 500;
  flex: 1;
  padding-top: 0.5em;
}

.material-symbols-outlined {
  font-size: 1.1rem;
  cursor: pointer;
}

.info_icon {
  color: var(--classic-text-muted, #9b9b9b);
  font-size: 1.05rem;
}

.label_container {
  display: flex;
  margin-bottom: 0.5em;
  align-items: center;
}

.label_container label {
  flex: 1;
  font-weight: 500;
}

.label_container .link {
  font-weight: normal;
}

.row {
  margin-bottom: 1.5em;
}

.row:last-child {
  margin-bottom: 0;
}

.row_with_border {
  border-top: 1px solid var(--classic-border-subtle, #e0e0e0);
  padding-top: 1.5em;
}

p.help {
  font-size: 0.9em;
  color: var(--classic-error-text, #f14668);
  margin: 0;
  padding: 0;
  margin-top: 0.25rem;
  height: 0.8em;
}

p.info {
  font-size: 0.85em;
  color: var(--classic-text-muted, #9b9b9b);
  margin: 0.5em 0 0 0;
  padding: 0;
  line-height: 1.4;
}

.checkbox_label {
  display: flex;
  align-items: center;
  gap: 0.5em;
  cursor: pointer;
  user-select: none;
}

input[type="checkbox"] {
  margin-right: 0.5em;
}

.languages_list {
  display: flex;
  flex-direction: column;
  gap: 0.5em;
}

.checkbox_label.disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.hcp_upload_settings {
  margin-top: 1.5em;
}

.hcp_upload_settings :deep([data-test="hcp-upload-settings"]) {
  border-color: var(--classic-border-subtle, #e0e0e0);
  padding-top: 1.5em;
}

.hcp_upload_settings :deep(button),
.hcp_upload_settings :deep(input),
.hcp_upload_settings :deep(select) {
  font-family: inherit;
}
</style>
